import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { Button, Field, FieldDescription, FieldLabel, Input, Switch, Textarea } from '@clouway/ui'

import { UnsavedChangesGuard } from '@/components/UnsavedChangesGuard'
import type { OAuthClientInput } from '../types'

const schema = z.object({
  description: z.string(),
  redirectURLs: z.string(),
  public: z.boolean(),
  forAccounts: z.boolean(),
})

type OAuthClientFormValues = z.infer<typeof schema>

// redirectURLs is edited as newline-separated text and split on submit.
const splitUrls = (value: string): string[] =>
  value
    .split('\n')
    .map((url) => url.trim())
    .filter((url) => url !== '')

export function OAuthClientForm({
  mode,
  initialValues,
  onSubmit,
}: {
  mode: 'create' | 'edit'
  initialValues?: OAuthClientInput
  onSubmit: (input: OAuthClientInput) => void | Promise<void>
}) {
  const { t } = useTranslation('feature')
  const { control, handleSubmit, formState } = useForm<OAuthClientFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      description: initialValues?.description ?? '',
      redirectURLs: (initialValues?.redirectURLs ?? []).join('\n'),
      public: initialValues?.public ?? false,
      forAccounts: initialValues?.forAccounts ?? false,
    },
  })
  const creating = mode === 'create'

  const submit = (values: OAuthClientFormValues) =>
    onSubmit({
      description: values.description,
      redirectURLs: splitUrls(values.redirectURLs),
      public: values.public,
      forAccounts: values.forAccounts,
    })

  return (
    <form className="mx-auto flex w-full max-w-2xl flex-col gap-4" onSubmit={handleSubmit(submit)}>
      <UnsavedChangesGuard when={formState.isDirty && !formState.isSubmitSuccessful} />

      <Controller
        control={control}
        name="description"
        render={({ field }) => (
          <Field>
            <FieldLabel htmlFor="client-description">{t('oauthClient.form.description')}</FieldLabel>
            <Input id="client-description" {...field} />
          </Field>
        )}
      />

      <Controller
        control={control}
        name="redirectURLs"
        render={({ field }) => (
          <Field>
            <FieldLabel htmlFor="client-redirects">{t('oauthClient.form.redirectURLs')}</FieldLabel>
            <Textarea id="client-redirects" rows={4} {...field} />
            <FieldDescription>{t('oauthClient.form.redirectURLsHelp')}</FieldDescription>
          </Field>
        )}
      />

      {creating && (
        <Controller
          control={control}
          name="public"
          render={({ field }) => (
            <Field orientation="horizontal">
              <Switch id="client-public" checked={field.value} onCheckedChange={field.onChange} />
              <FieldLabel htmlFor="client-public">{t('oauthClient.form.public')}</FieldLabel>
            </Field>
          )}
        />
      )}

      <Controller
        control={control}
        name="forAccounts"
        render={({ field }) => (
          <Field orientation="horizontal">
            <Switch id="client-for-accounts" checked={field.value} onCheckedChange={field.onChange} />
            <FieldLabel htmlFor="client-for-accounts">{t('oauthClient.form.forAccounts')}</FieldLabel>
          </Field>
        )}
      />

      <Button type="submit" disabled={formState.isSubmitting}>
        {formState.isSubmitting
          ? t(creating ? 'oauthClient.form.creating' : 'oauthClient.form.submitting')
          : t(creating ? 'oauthClient.form.create' : 'oauthClient.form.submit')}
      </Button>
    </form>
  )
}
